/**
 * src/pages/Login.jsx
 * Acceso al panel admin por usuario/contraseña (JWT).
 * El token maestro estático ya NO se introduce ni se guarda en el navegador:
 * el panel se autentica con el JWT de sesión de un usuario con rol 'admin'.
 *
 * Diseño: identidad de marca CM Monitor (Oxford / Coral / Outfit).
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { api } from '../api/client'

export default function Login() {
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [verClave, setVerClave] = useState(false)
  const [error, setError]       = useState('')
  const [loading, setLoading]   = useState(false)
  const setJwt                  = useAuthStore(state => state.setJwt)
  const navigate                = useNavigate()

  async function handleSubmit(e) {
    e.preventDefault()
    if (!email.trim() || !password) {
      setError('Ingresa tu email y contraseña')
      return
    }

    setLoading(true)
    setError('')

    try {
      const { data } = await api.post('/auth/login', {
        email: email.trim(),
        password,
      })
      if (data.rol !== 'admin') {
        setError('Esta cuenta no tiene permisos de administrador.')
        return
      }
      setJwt(data.access_token, {
        email: data.email, nombre: data.nombre, rol: data.rol,
      })
      navigate('/', { replace: true })
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Email o contraseña incorrectos.')
      } else if (err.response?.status === 403) {
        setError('Cuenta desactivada.')
      } else if (err.code === 'ERR_NETWORK' || err.isNetworkError) {
        setError('No se puede conectar al servidor.')
      } else {
        setError('Error al conectar: ' + (err.message || 'desconocido'))
      }
    } finally {
      setLoading(false)
    }
  }

  const inputStyle = (hasError) => ({
    width: '100%',
    background: 'var(--color-surface-2)',
    border: `1px solid ${hasError ? 'var(--color-error)' : 'var(--color-border)'}`,
    borderRadius: 'var(--radius-md)',
    padding: '10px 14px',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-ui)',
    color: 'var(--color-text)',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  })

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--space-5)',
    }}>
      {/* Background grid pattern */}
      <div style={{
        position: 'fixed', inset: 0, opacity: 0.04,
        backgroundImage: 'linear-gradient(var(--color-accent) 1px, transparent 1px), linear-gradient(90deg, var(--color-accent) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        pointerEvents: 'none',
      }} />

      <div style={{ width: '100%', maxWidth: '400px', position: 'relative' }}>
        {/* Logo / Branding */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-7)' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '11px',
            marginBottom: 'var(--space-2)',
          }}>
            <div style={{
              width: '34px', height: '34px', borderRadius: '7px',
              background: 'var(--color-accent)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </div>
            <span style={{
              fontFamily: 'var(--font-brand)', fontWeight: 700,
              fontSize: '20px', color: 'var(--color-text)',
              letterSpacing: '-0.02em',
            }}>
              CM Monitor
            </span>
          </div>
          <p style={{
            fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-ui)', letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}>
            Convenio Marco · Mercado Público
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-6)',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <h1 style={{
            fontFamily: 'var(--font-brand)',
            fontSize: '21px', fontWeight: 600, color: 'var(--color-text)',
            marginBottom: 'var(--space-1)', letterSpacing: '-0.01em',
          }}>
            Acceso al panel
          </h1>
          <p style={{
            fontSize: 'var(--text-base)', color: 'var(--color-text-2)',
            marginBottom: '28px',
          }}>
            Ingresa con tu cuenta de administrador
          </p>

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{
                display: 'block', fontSize: 'var(--text-sm)', fontWeight: 600,
                color: 'var(--color-text-2)', marginBottom: '6px',
                fontFamily: 'var(--font-ui)', letterSpacing: '0.03em',
              }}>
                EMAIL
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@cmmonitor.cl"
                autoComplete="username"
                disabled={loading}
                style={inputStyle(error)}
                onFocus={e => { e.target.style.borderColor = 'var(--color-accent)' }}
                onBlur={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-border)' }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block', fontSize: 'var(--text-sm)', fontWeight: 600,
                color: 'var(--color-text-2)', marginBottom: '6px',
                fontFamily: 'var(--font-ui)', letterSpacing: '0.03em',
              }}>
                CONTRASEÑA
              </label>
              {/* El ojo (Manuel, 29-08): mismo icono y gesto que Campo.jsx de
                  las pantallas públicas — ver lo tecleado antes de enviar. */}
              <div style={{ position: 'relative' }}>
                <input
                  type={verClave ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  disabled={loading}
                  style={{ ...inputStyle(error), paddingRight: '44px' }}
                  onFocus={e => { e.target.style.borderColor = 'var(--color-accent)' }}
                  onBlur={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-border)' }}
                />
                <button
                  type="button"
                  onClick={() => setVerClave(v => !v)}
                  aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  style={{
                    position: 'absolute', right: '4px', top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '36px', height: '36px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--color-text-2)',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
                    <circle cx="12" cy="12" r="2.6" />
                    {verClave && <path d="m3 21 18-18" />}
                  </svg>
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: 'var(--color-error-soft)', border: '1px solid var(--color-error-line)',
                borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 'var(--space-4)',
                fontSize: 'var(--text-sm)', color: 'var(--color-error)',
                fontFamily: 'var(--font-ui)',
              }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: 'var(--color-accent)',
                color: 'var(--color-on-accent)',
                border: 'none', borderRadius: 'var(--radius-md)',
                padding: '11px', fontSize: 'var(--text-md)', fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
                fontFamily: 'var(--font-ui)',
                letterSpacing: '0.01em',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--color-accent-2)' }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = 'var(--color-accent)' }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: '14px', height: '14px', border: '2px solid currentColor',
                    borderTopColor: 'transparent', borderRadius: '50%',
                    display: 'inline-block', animation: 'spin 0.7s linear infinite',
                  }} />
                  Verificando...
                </>
              ) : 'Acceder'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '14px', marginBottom: 0, fontSize: 'var(--text-base)' }}>
            <Link to="/forgot-password" style={{ color: 'var(--color-text-3)', textDecoration: 'none' }}>¿Olvidaste tu contraseña?</Link>
          </p>
        </div>

        {/* Footer */}
        <p style={{
          textAlign: 'center', marginTop: '20px', fontSize: 'var(--text-xs)',
          color: 'var(--color-text-3)', fontFamily: 'var(--font-ui)',
        }}>
          CM Monitor · Uso interno
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: var(--color-text-3); }
      `}</style>
    </div>
  )
}
